import React, { Component } from "react";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import BaseCard from "../../Cards";
import PropTypes from "prop-types";
import ActionButton from "../../Station/buttons/actionButton";
import FullPageCard from "../Components/FullPageCard";
import { Back } from "../../Buttons";
import Container from "../../WYSIWYG/Container";

const StyledRightContentContainer = styled(Container)`
    flex: 1;
    overflow-y: auto;
`;

const viewStates = { consent: "consent", terms: "terms", policy: "policy" };

// Consent View
const StyledContainer = styled.div`
    display: flex;
    align-items: stretch;
    align-content: stretch;
    min-height: 100%;
    height: 100%;
`;

const StyledLeftContainer = styled(BaseCard)`
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex-grow: 3;
    flex-basis: 1em;
    margin-inline-end: 1em;
    padding: 0;
    height: 100%;
    width: 100%;

    > .content {
        position: relative;
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
    }
`;

const StyledImage = styled.div`
    background: transparent url("${props => props.source}") no-repeat center center;
    resize: both;
    background-size: contain;
    flex: 1 1 auto;
    min-height: 0;
    max-height: 100%;
    width: 100%;
    border-radius: 5px 5px 0 0;
`;

const StyledRightContainer = styled(BaseCard)`
    position: relative;
    flex-grow: 5;
    flex-basis: 2em;
    margin-inline-start: 1em;
    min-height: 100%;
    height: 100%;
    padding: 0;
    display: flex;
    flex-direction: column;

    > .content {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
`;

const StyledButtonContainer = styled.div`
    width: 100%;
    height: 150px;
    border-top: 1px solid ${props => props.theme.colours.flat.lightGray.hex};
    text-align: center;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: space-between;
    background: #fff;
    padding: 15px;
    gap: 15px;
    box-sizing: border-box;
    flex-shrink: 0;
`;

const StyledTermsPrivacyContainer = styled(StyledButtonContainer)`
    flex-direction: column;
    position: relative;
    height: auto;
    min-height: 0;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 -6px 12px rgba(0, 0, 0, 0.08);
    justify-content: flex-start;
    flex-shrink: 0;
`;

const StyledActionButton = styled(ActionButton)`
    text-align: center !important;
    flex: 1 1 0;
    max-width: none;
    height: 100%;
    max-height: unset;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.2;
    font-size: clamp(1.5rem, 3vw, 2.25rem);
    font-weight: 600;
`;

const StyledTermsButton = styled(StyledActionButton)`
    height: auto;
    max-height: none;
    min-height: ${props => props.theme.actionButtons.height};
    font-size: clamp(1rem, 2vw, 1.5rem);
    padding: 0.5rem 1rem;
`;

// Terms and Policy
const StyledTermsContainer = styled(StyledContainer)`
    flex-direction: column;
    width: 100%;
    padding: 15px;
`;

// contains WYSIWYG content
const StyledTermsDetails = styled.div`
    margin-top: 1em;
    width: 100%;
    height: 670px;
    overflow: scroll;

    h2 {
        font-family: "Proxima Nova Semibold", Arial, sans-serif;
        font-size: ${props => props.theme.text.heading2.sizeRem};
        margin: 0 0 0.8em;
        padding: 0;
        line-height: 1.3em;
    }

    h3 {
        font-family: "Proxima Nova Semibold", Arial, sans-serif;
        font-size: ${props => props.theme.text.heading3.sizeRem};
        margin: 0 0 0.8em;
        padding: 0;
        line-height: 1.3em;
    }

    h4 {
        font-family: "Proxima Nova Semibold", Arial, sans-serif;
        font-size: ${props => props.theme.text.heading4.sizeRem};
        margin: 0 0 0.8em;
        padding: 0;
        line-height: 1.3em;
    }

    p {
        font-size: ${props => props.theme.text.paragraph.sizeRem};
        margin: 0 0 0.8em;
        padding: 0;
        line-height: 1.3em;
    }
`;

const StyledVideo = styled.video`
    width: 100%;
    height: 100%;
    min-height: 0;
    flex: 1 1 auto;
    object-fit: contain;
    overflow: hidden;
    max-width: none;
    background-color: transparent;
    border-radius: 5px 5px 0 0;
`;

class TemplateOne extends Component {
    static propTypes = {
        theme: PropTypes.object,
        schema: PropTypes.object.isRequired,
        onAccepted: PropTypes.func.isRequired,
        onRejected: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            view: viewStates.consent
        };

        this.handleConsentClick = this.handleConsentClick.bind(this);
        this.handlePolicyClick = this.handlePolicyClick.bind(this);
        this.handleTermsClick = this.handleTermsClick.bind(this);
        this.handleAccepted = this.handleAccepted.bind(this);
        this.handleRejected = this.handleRejected.bind(this);
    }

    handleConsentClick(event) {
        event.preventDefault();

        this.setState({ view: viewStates.consent });
    }

    handleTermsClick(event) {
        event.preventDefault();

        this.setState({ view: viewStates.terms });
    }

    handlePolicyClick(event) {
        event.preventDefault();

        this.setState({ view: viewStates.policy });
    }

    handleAccepted() {
        this.props.onAccepted();
    }

    handleRejected() {
        this.props.onRejected();
    }

    compositeConsentView() {
        const { t } = this.props;
        let {
            acceptButtonLabel,
            rejectButtonLabel,
            upSell,
            primaryImage,
            termsAndConditions,
            termsAndConditionsButtonLabel = "Terms of Use",
            privacyPolicy,
            privacyPolicyButtonLabel = "Privacy Policy"
        } = this.props.schema;

        acceptButtonLabel = t(acceptButtonLabel);
        rejectButtonLabel = t(rejectButtonLabel);
        termsAndConditionsButtonLabel = t(termsAndConditionsButtonLabel);
        privacyPolicyButtonLabel = t(privacyPolicyButtonLabel);

        rejectButtonLabel = rejectButtonLabel ? rejectButtonLabel.trim() : "";

        const hasPrivacyPolicy =
            typeof privacyPolicy === "string" ? privacyPolicy.trim().length > 0 : Boolean(privacyPolicy);
        const hasTerms =
            typeof termsAndConditions === "string" ? termsAndConditions.trim().length > 0 : Boolean(termsAndConditions);

        let privacyPolicyLink;
        if (hasPrivacyPolicy) {
            privacyPolicyLink = (
                <StyledTermsButton
                    styleType={"Secondary"}
                    label={privacyPolicyButtonLabel}
                    onClick={this.handlePolicyClick}
                />
            );
        }

        let termsAndConditionsLink;
        if (hasTerms) {
            termsAndConditionsLink = (
                <StyledTermsButton
                    styleType={"Secondary"}
                    label={termsAndConditionsButtonLabel}
                    onClick={this.handleTermsClick}
                />
            );
        }

        const assetType = /(?:.mp4$)|(?:.webm$)|(?:.ogg$)/.test(primaryImage) ? "video" : "image";

        return (
            <StyledContainer>
                <StyledLeftContainer>
                    {assetType === "video" ? (
                        <StyledVideo autoPlay muted data-testid="video-asset">
                            <source src={primaryImage} type="video/mp4" />
                        </StyledVideo>
                    ) : (
                        <StyledImage source={primaryImage} data-testid="image-asset" />
                    )}

                    {(termsAndConditionsLink || privacyPolicyLink) && (
                        <StyledTermsPrivacyContainer data-testid="tc-container">
                            {termsAndConditionsLink}
                            {privacyPolicyLink}
                        </StyledTermsPrivacyContainer>
                    )}
                </StyledLeftContainer>
                <StyledRightContainer>
                    <StyledRightContentContainer dangerouslySetInnerHTML={{ __html: upSell }} />
                    <StyledButtonContainer>
                        {rejectButtonLabel && (
                            <StyledActionButton
                                styleType={"Secondary"}
                                label={rejectButtonLabel}
                                onClick={this.handleRejected}
                            />
                        )}
                        <StyledActionButton label={acceptButtonLabel} onClick={this.handleAccepted} />
                    </StyledButtonContainer>
                </StyledRightContainer>
            </StyledContainer>
        );
    }

    compositeTerms() {
        return this.compositeFullPage(this.props.schema.termsAndConditions);
    }

    compositePolicy() {
        return this.compositeFullPage(this.props.schema.privacyPolicy);
    }

    compositeFullPage(wysiwygContent) {
        const { t } = this.props;
        const backLabel = t("wrd.back");

        return (
            <FullPageCard>
                <StyledTermsContainer>
                    <Back visible onClick={this.handleConsentClick}>
                        {`< ${backLabel}`}
                    </Back>
                    <StyledTermsDetails dangerouslySetInnerHTML={{ __html: wysiwygContent }} />
                </StyledTermsContainer>
            </FullPageCard>
        );
    }

    render() {
        let output;
        switch (this.state.view) {
            case viewStates.consent:
                output = this.compositeConsentView();
                break;

            case viewStates.terms:
                output = this.compositeTerms();
                break;

            case viewStates.policy:
                output = this.compositePolicy();
                break;
        }

        return output;
    }
}

export default withTranslation()(TemplateOne);
